/**
 * 语言生成器加载器
 * 负责动态加载和缓存不同语言的代码生成器
 */

import { languages } from '../../data/languages.js';
import { PythonGenerator } from '../generators/python.js';
import { JavaScriptGenerator } from '../generators/javascript.js';
import { PhpGenerator } from '../generators/php.js';
import { JavaGenerator } from '../generators/java.js';
import { GoGenerator } from '../generators/go.js';

export class LanguageLoader {
  constructor() {
    this.generators = new Map();
    this.loadingPromises = new Map();
    this.initialized = false;
  }

  /**
   * 初始化加载器
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.initialized) {
      return;
    }

    // 预加载常用的生成器
    const preloadLanguages = ['python', 'javascript'];

    const preloadPromises = preloadLanguages.map(langId =>
      this.loadGenerator(langId)
    );

    await Promise.all(preloadPromises);
    this.initialized = true;
  }

  /**
   * 加载指定语言的生成器
   * @param {string} languageId - 语言 ID
   * @returns {Promise<BaseCodeGenerator>} 生成器实例
   */
  async loadGenerator(languageId) {
    // 检查缓存
    if (this.generators.has(languageId)) {
      return this.generators.get(languageId);
    }

    // 检查是否正在加载
    if (this.loadingPromises.has(languageId)) {
      return this.loadingPromises.get(languageId);
    }

    // 开始加载
    const loadPromise = this.doLoadGenerator(languageId);
    this.loadingPromises.set(languageId, loadPromise);

    try {
      const generator = await loadPromise;
      this.generators.set(languageId, generator);
      return generator;
    } finally {
      this.loadingPromises.delete(languageId);
    }
  }

  /**
   * 执行生成器加载
   * @param {string} languageId - 语言 ID
   * @returns {Promise<BaseCodeGenerator>} 生成器实例
   */
  async doLoadGenerator(languageId) {
    const languageConfig = languages.find(lang => lang.id === languageId);

    if (!languageConfig) {
      throw new Error(`不支持的语言: ${languageId}`);
    }

    // 根据语言 ID 创建对应的生成器
    let generator;

    switch (languageId) {
      case 'python':
        generator = new PythonGenerator();
        break;

      case 'javascript':
        generator = new JavaScriptGenerator();
        break;

      case 'nodejs':
        generator = new JavaScriptGenerator();
        generator.config.library = 'axios';
        break;

      case 'php':
        generator = new PhpGenerator();
        break;

      case 'java':
        generator = new JavaGenerator();
        break;

      case 'go':
        generator = new GoGenerator();
        break;

      default:
        generator = await this.loadDynamicGenerator(languageId);
        break;
    }

    return generator;
  }

  /**
   * 动态加载生成器（用于扩展）
   * @param {string} languageId - 语言 ID
   * @returns {Promise<BaseCodeGenerator>} 生成器实例
   */
  async loadDynamicGenerator(languageId) {
    try {
      // 动态导入生成器模块
      const module = await import(`../generators/${languageId}.js`);
      const GeneratorClass = module[`${languageId.charAt(0).toUpperCase() + languageId.slice(1)}Generator`];

      if (!GeneratorClass) {
        throw new Error(`找不到生成器类: ${languageId}`);
      }

      return new GeneratorClass();
    } catch (error) {
      // 如果生成器不存在，返回一个占位符生成器
      console.warn(`生成器 ${languageId} 暂未实现，使用占位符`);
      return this.createPlaceholderGenerator(languageId);
    }
  }

  /**
   * 创建占位符生成器
   * @param {string} languageId - 语言 ID
   * @returns {Object} 占位符生成器
   */
  createPlaceholderGenerator(languageId) {
    const languageConfig = languages.find(lang => lang.id === languageId);
    const languageName = languageConfig ? languageConfig.displayName : languageId;
    
    return {
      generate: (parsedCurl) => {
        return `// ${languageName} 代码生成器正在开发中
// 敬请期待！

// 原始 curl 命令:
// ${this.reconstructCurlCommand(parsedCurl)}

// 请暂时使用其他语言，或手动转换此命令`;
      }
    };
  }

  /**
   * 重构 curl 命令（用于占位符显示）
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 重构的 curl 命令
   */
  reconstructCurlCommand(parsedCurl) {
    let command = `curl -X ${parsedCurl.method} '${parsedCurl.url}'`;
    
    // 添加请求头
    Object.entries(parsedCurl.headers).forEach(([key, value]) => {
      command += ` -H '${key}: ${value}'`;
    });
    
    // 添加数据
    if (parsedCurl.data.type !== 'none' && parsedCurl.data.content) {
      command += ` -d '${parsedCurl.data.content}'`;
    }
    
    return command;
  }

  /**
   * 获取已加载的生成器
   * @param {string} languageId - 语言 ID
   * @returns {BaseCodeGenerator|null} 生成器实例或 null
   */
  getGenerator(languageId) {
    return this.generators.get(languageId) || null;
  }

  /**
   * 检查生成器是否已加载
   * @param {string} languageId - 语言 ID
   * @returns {boolean} 是否已加载
   */
  isLoaded(languageId) {
    return this.generators.has(languageId);
  }

  /**
   * 检查生成器是否正在加载
   * @param {string} languageId - 语言 ID
   * @returns {boolean} 是否正在加载
   */
  isLoading(languageId) {
    return this.loadingPromises.has(languageId);
  }

  /**
   * 获取所有已加载的语言 ID
   * @returns {Array} 语言 ID 数组
   */
  getLoadedLanguages() {
    return Array.from(this.generators.keys());
  }

  /**
   * 卸载生成器
   * @param {string} languageId - 语言 ID
   */
  unloadGenerator(languageId) {
    this.generators.delete(languageId);
  }

  /**
   * 清除所有缓存的生成器
   */
  clearCache() {
    this.generators.clear();
    this.loadingPromises.clear();
    this.initialized = false;
  }

  /**
   * 获取加载统计信息
   * @returns {Object} 统计信息
   */
  getStats() {
    return {
      loaded: this.generators.size,
      loading: this.loadingPromises.size,
      initialized: this.initialized,
      loadedLanguages: this.getLoadedLanguages()
    };
  }

  /**
   * 预加载所有支持的语言
   * @returns {Promise<Array>} 生成器实例数组
   */
  async preloadAllGenerators() {
    const loadPromises = languages.map(lang =>
      this.loadGenerator(lang.id)
    );

    return Promise.all(loadPromises);
  }

  /**
   * 批量加载生成器
   * @param {Array} languageIds - 语言 ID 数组
   * @returns {Promise<Array>} 生成器实例数组
   */
  async loadGenerators(languageIds) {
    const loadPromises = languageIds.map(langId =>
      this.loadGenerator(langId)
    );

    return Promise.all(loadPromises);
  }
}